/**
 * The tokens as data, for code that has to list or read them (the showcase does).
 * The values themselves live in styles/tokens.css; tokens.test.ts checks that the
 * device table below says what the stylesheet says.
 */

export const DEVICES = ['desktop', 'tablet', 'mobile'] as const;
export type Device = (typeof DEVICES)[number];

export const BREAKPOINTS: Record<Device, { width: number; range: string; columns: number; margin: number; gutter: number }> = {
  desktop: { width: 1280, range: '≥ 1280 px', columns: 12, margin: 48, gutter: 24 },
  tablet: { width: 768, range: '768 – 1279 px', columns: 8, margin: 32, gutter: 20 },
  mobile: { width: 390, range: '≤ 767 px', columns: 4, margin: 20, gutter: 12 },
};

/** The device a viewport of this width is, by the same limits as the stylesheet. */
export function deviceForWidth(width: number): Device {
  if (width <= 767) return 'mobile';
  return width <= 1279 ? 'tablet' : 'desktop';
}

export const COLOR_GROUPS: { title: string; tokens: string[] }[] = [
  { title: 'Поверхности', tokens: ['bg-canvas', 'bg-surface', 'bg-sunken', 'bg-muted', 'bg-subtle', 'bg-inverse-soft', 'bg-inverse'] },
  { title: 'Текст и границы', tokens: ['text-primary', 'text-secondary', 'text-tertiary', 'text-on-inverse', 'border-default', 'border-strong'] },
  { title: 'Primary · лаванда', tokens: ['primary-default', 'primary-hover', 'primary-container', 'primary-on-container', 'primary-deep'] },
  {
    title: 'Secondary · олива',
    tokens: ['secondary-default', 'secondary-hover', 'secondary-soft', 'secondary-camo', 'secondary-container', 'secondary-container-soft', 'secondary-on-container', 'secondary-deep', 'secondary-tint'],
  },
  {
    title: 'Состояния · ошибка и предупреждение',
    tokens: ['danger-default', 'danger-container', 'danger-on-container', 'warning-default', 'warning-container', 'warning-on-container'],
  },
];

export const SPACING = [1, 2, 3, 4, 5, 6, 8, 10, 12, 16, 24] as const;
export const RADII = ['sm', 'md', 'lg', 'xl', '2xl', 'full'] as const;
export const SHADOWS = ['shadow-xs', 'shadow-sm', 'shadow-md', 'shadow-xl', 'focus-lavender', 'focus-olive', 'glow-olive'] as const;

export const TYPE_STYLES: { name: string; className: string; sample: string; family: string }[] = [
  { name: 'Display', className: 'hs-display', sample: 'Модальные лады и слух', family: 'EB Garamond · Medium' },
  { name: 'H2', className: 'hs-h2', sample: 'Ступени лада: ионийский', family: 'EB Garamond · SemiBold' },
  { name: 'H3', className: 'hs-h3', sample: 'Интервальная точность скачка', family: 'EB Garamond · SemiBold' },
  { name: 'Numeric', className: 'hs-numeric', sample: '440.00', family: 'EB Garamond · Medium' },
  { name: 'Body L', className: 'hs-body-l', sample: 'Чистая темперация строится на резонансе гармоник.', family: 'Manrope · Regular' },
  { name: 'Body', className: 'hs-body', sample: 'Система фиксирует отклонение частоты в реальном времени.', family: 'Manrope · Regular' },
  { name: 'Body S', className: 'hs-body-s', sample: 'Распознавание голоса и клавиатурный ввод знаков.', family: 'Manrope · Regular' },
  { name: 'Label', className: 'hs-label', sample: 'Спеть в ладу', family: 'Manrope · SemiBold' },
  { name: 'Caption', className: 'hs-caption', sample: 'Прогресс ступени', family: 'Manrope · Bold' },
  { name: 'Overline', className: 'hs-overline', sample: 'Выбор активной ступени', family: 'Manrope · Bold' },
  { name: 'Mono', className: 'hs-mono', sample: 'G4 Sol • 392.2 Hz', family: 'JetBrains Mono · Medium' },
];

/** Sizes that differ per device, by token name without the `--hs-` prefix. */
export const DEVICE_TOKENS: Record<Device, Record<string, string>> = {
  desktop: {
    'display-size': '36px',
    'display-lh': '44px',
    'h2-size': '24px',
    'h2-lh': '32px',
    'h3-size': '20px',
    'h3-lh': '28px',
    'numeric-size': '32px',
    'numeric-lh': '36px',
    'body-l-size': '18px',
    'body-l-lh': '28px',
    'body-size': '15px',
    'body-lh': '24px',
    'label-size': '12px',
    'label-lh': '16px',
    'label-ls': '0.04em',
    'caption-size': '11px',
    'caption-lh': '14px',
    'caption-ls': '0.05em',
    'mono-size': '12px',
    'mono-lh': '16px',
    'button-pad-y': '10px',
    'button-pad-x': '16px',
    'button-icon': '16px',
    'icon-button-size': '36px',
    'icon-button-icon': '16px',
    'chip-pad-y': '4px',
    'chip-pad-x': '12px',
    'chip-icon': '12px',
    'field-height': '44px',
    'field-icon': '18px',
    'toggle-width': '44px',
    'toggle-height': '24px',
    'segment-pad-y': '6px',
    'segment-pad-x': '14px',
    'degree-width': '56px',
    'degree-height': '52px',
    'card-pad': '24px',
    'tile-pad': '20px',
    'tile-gap': '32px',
    'page-margin': '48px',
    'page-gutter': '24px',
    'topbar-nav-display': 'flex',
    'topbar-more-display': 'inline-flex',
    'topbar-brand-display': 'flex',
    'topbar-section-display': 'none',
    'topbar-pitch-display': 'inline-flex',
    'topbar-menu-display': 'none',
    'tuner-direction': 'row',
    'tuner-align': 'center',
    'tuner-minor-display': 'inline',
  },
  tablet: {
    'display-size': '32px',
    'display-lh': '40px',
    'h2-size': '22px',
    'h2-lh': '30px',
    'h3-size': '20px',
    'h3-lh': '28px',
    'numeric-size': '30px',
    'numeric-lh': '34px',
    'body-l-size': '17px',
    'body-l-lh': '26px',
    'body-size': '15px',
    'body-lh': '24px',
    'label-size': '13px',
    'label-lh': '18px',
    'label-ls': '0.03em',
    'caption-size': '11px',
    'caption-lh': '14px',
    'caption-ls': '0.05em',
    'mono-size': '12px',
    'mono-lh': '16px',
    'button-pad-y': '11px',
    'button-pad-x': '20px',
    'button-icon': '18px',
    'icon-button-size': '40px',
    'icon-button-icon': '18px',
    'chip-pad-y': '5px',
    'chip-pad-x': '12px',
    'chip-icon': '12px',
    'field-height': '48px',
    'field-icon': '20px',
    'toggle-width': '48px',
    'toggle-height': '28px',
    'segment-pad-y': '8px',
    'segment-pad-x': '16px',
    'degree-width': '60px',
    'degree-height': '56px',
    'card-pad': '24px',
    'tile-pad': '20px',
    'tile-gap': '32px',
    'page-margin': '32px',
    'page-gutter': '20px',
    'topbar-nav-display': 'flex',
    'topbar-more-display': 'none',
    'topbar-brand-display': 'flex',
    'topbar-section-display': 'none',
    'topbar-pitch-display': 'none',
    'topbar-menu-display': 'inline-flex',
    'tuner-direction': 'row',
    'tuner-align': 'center',
    'tuner-minor-display': 'inline',
  },
  mobile: {
    'display-size': '28px',
    'display-lh': '36px',
    'h2-size': '20px',
    'h2-lh': '28px',
    'h3-size': '18px',
    'h3-lh': '26px',
    'numeric-size': '28px',
    'numeric-lh': '32px',
    'body-l-size': '16px',
    'body-l-lh': '24px',
    'body-size': '15px',
    'body-lh': '22px',
    'label-size': '14px',
    'label-lh': '20px',
    'label-ls': '0.02em',
    'caption-size': '12px',
    'caption-lh': '16px',
    'caption-ls': '0.04em',
    'mono-size': '13px',
    'mono-lh': '18px',
    'button-pad-y': '12px',
    'button-pad-x': '24px',
    'button-icon': '18px',
    'icon-button-size': '44px',
    'icon-button-icon': '20px',
    'chip-pad-y': '6px',
    'chip-pad-x': '14px',
    'chip-icon': '14px',
    'field-height': '52px',
    'field-icon': '20px',
    'toggle-width': '52px',
    'toggle-height': '32px',
    'segment-pad-y': '10px',
    'segment-pad-x': '16px',
    'degree-width': '44px',
    'degree-height': '52px',
    'card-pad': '20px',
    'tile-pad': '16px',
    'tile-gap': '20px',
    'page-margin': '20px',
    'page-gutter': '12px',
    'topbar-nav-display': 'none',
    'topbar-more-display': 'none',
    'topbar-brand-display': 'none',
    'topbar-section-display': 'flex',
    'topbar-pitch-display': 'none',
    'topbar-menu-display': 'inline-flex',
    'tuner-direction': 'column',
    'tuner-align': 'flex-start',
    'tuner-minor-display': 'none',
  },
};
